
import Page from '@root/common/base/Page';
import { Data as ArticleData, Headlines, State } from '@root/modules/article/types/article';
import { RelatedArticle } from '@root/common/types/fragment';
import ArticleService from '@root/modules/article/services/Article.service';
import ArticleExtraContentService from '@root/modules/article/services/ArticleExtraContent.service';

import ArticleSkeletonLoader from '@root/views/article/components/ArticleSkeletonLoader.vue';
import articleJsonld from '@root/views/article/utils/articleJsonld';

import ArticleContent from '@root/views/article/components/ArticleContent.vue';
import ArticleContentVideo from '@root/views/article/components/ArticleContentVideo.vue';
import { getArticleMeta } from '@root/views/article/utils/articleMeta';

import getArticleAdsSettings from '@root/views/article/utils/getArticleAdsSettings';
import isArticleMultiPage from '@root/views/article/utils/isArticleMultipage';
import getFakeArticleUrl from '@root/modules/article/utils/getFakeArticleUrl';

import ArticleCxenseService from '@root/modules/article/services/ArticleCxense.service';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';

interface Data {
  article: ArticleData | null;
  headlines: RelatedArticle[];
  requestFailed: boolean;
  isArticleFetching: boolean;
  isRefetched: boolean;
  isPrefetched: boolean;
  shouldBeRefetched: boolean;
}

interface Props {
  articleId: number;
  initialArticle: boolean;
}

interface Computed {
  localArticle: ArticleData | null;
  customerToken: string | null;
  isFetchPending: boolean;
  isVideoArticle: boolean;
  isMultimedijaArticle: boolean;
}

interface Methods {
  handleArticleLoad: (articleId: string) => void;
  refetchArticle: (doRefetch: boolean) => void;
  handleArticleRequestFailure: ({
    article,
    access,
    articleToken,
    customerToken,
  }: {
    article: ArticleData;
    access: boolean;
    articleToken: string | null;
    customerToken: string | null;
  }) => void;
  getArticleHeadlines: (relatedHeadlineIds: number[], limit?: number) => Promise<Headlines>;
}

export default Page.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleSkeletonLoader,
    ArticleContent,
    ArticleContentVideo,
  },
  validate({ params }) {
    return /^\d+$/.test(params.id) && /^[a-z0-9-]+$/.test(params.slug);
  },
  props: {
    articleId: {
      type: Number,
      required: true,
    },
    initialArticle: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      article: this.$store.getters['article/getTempData']?.article || null,
      headlines: [],
      meta: null,
      requestFailed: false,
      isArticleFetching: true,
      isRefetched: false,
      isPrefetched: false,
      shouldBeRefetched: false,
    };
  },
  async fetch() {
    if (this.$store.state.article.preFetch) {
      this.$store.commit('article/resetTempData');
      this.$store.commit('article/setPreFetch', false);
      this.isArticleFetching = true;
      this.isRefetched = true;
      this.isPrefetched = true;
      this.refetchArticle(true);
      return;
    }

    this.isArticleFetching = true;
    const channelConfig = this.$channelConfig('config');
    const { externalId: channelId, application, lang } = channelConfig.settings;
    const { articleId, customerToken, $route } = this;

    // Check if customer has rights to read the article
    try {
      const tempArticleData: State['tempData'] = this.$store.getters['article/getTempData'];
      const articleBaseDataExists = !!(!this.isPrefetched && this.article && this.article.content.body.content.length === 0);
      const requestBody = process.client && !!(customerToken || application.specialFeatures.forceArticleRefetch || $route.query.preview);
      const variables = {
        id: articleId,
        language: lang.toUpperCase(),
        channelId,
        customerToken,
        application,
        preview: $route.query.preview as string,
        settings: {
          articleBaseDataExists,
          requestBody,
          authorizationHeader: this.article?.content.paywall.enabled ?? true,
        },
      };

      const articleService = new ArticleService({ req: this.$ssrContext?.req });
      const articleData =
        tempArticleData ||
        (await articleService.fetch({
          variables,
          channelConfig,
        }));

      if (tempArticleData) {
        this.$store.commit('article/resetTempData');
      }

      if (!articleData?.article) {
        articleService.handleError({ message: 'article.error.not_found', statusCode: 404 });
        return;
      }

      const { article, access, articleToken } = articleData;

      if (article.settings.fakeArticle) {
        const fakeArticleUrl = getFakeArticleUrl(article);
        if (fakeArticleUrl) {
          this.$nuxt.context.redirect(302, fakeArticleUrl);
          return;
        }
      }

      if (isArticleMultiPage(article)) {
        article.settings.isMultiPage = true;
      }

      this.setPageAdsSettings(getArticleAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article }));
      this.$store.commit('article/setActiveArticle', article);
      this.$store.commit('category/setCategoryData', article.primaryCategory);

      if (this.isPrefetched) {
        this.$store.commit('adForm/setAdsRenderingState', true);
      }

      if (this.initialArticle) {
        this.$store.commit('article/setActiveArticlePath', this.$route.fullPath);
      }

      // Get related headlines for article bottom section
      if (this.headlines.length === 0 && article.relatedEntities.articles?.items) {
        this.headlines = article.relatedEntities.articles.items.map((relatedItem) => relatedItem.article) as RelatedArticle[];
      }

      if (!access && articleBaseDataExists) {
        this.isArticleFetching = false;
        return;
      }

      // Update only article body (fire this check on initialArticle CSR load)
      if (access && articleBaseDataExists) {
        this.article!.voiceSettings = article.voiceSettings;
        this.article!.content.lead.content = article.content.lead.content;
        this.article!.content.leadElement.content = article.content.leadElement.content;
        this.article!.content.body.content = article.content.body.content;
        this.article!.content.groupedBody = article.content.groupedBody;
        this.article!.content.paywall.access = article.content.paywall.access;
        this.isArticleFetching = false;
        return;
      }

      this.article = article;

      this.handleArticleRequestFailure({ article, access, articleToken, customerToken });
    } catch (e) {
      if (this.initialArticle) {
        this.handlePageError(e);
        return;
      }

      this.$emit('article-loaded', false);
    }

    if (process.client) {
      this.$nextTick(() => {
        this.isArticleFetching = false;
      });
    }
  },
  head() {
    if (this.article) {
      const meta = getArticleMeta({ $channelConfig: this.$channelConfig, article: this.article, path: this.$route.path });
      return {
        title: meta.title,
        meta: meta.data,
        style: meta.style,
        link: meta.link,
        __dangerouslyDisableSanitizers: ['style'],
      };
    }
    return {};
  },
  computed: {
    localArticle() {
      return this.article;
    },
    customerToken() {
      return this.$instanceConnector('customer').state.token;
    },
    isFetchPending() {
      return this.$fetchState?.pending;
    },
    isVideoArticle() {
      return this.article?.primaryCategory.channel.url === 'www.delfi.lt/video';
    },
    isMultimedijaArticle() {
      const { id } = this.$channelConfig('settings');
      return id === 'multimedija';
    },
  },
  watch: {
    requestFailed(reFetch) {
      const doRefetch = reFetch && process.client;
      this.refetchArticle(doRefetch);
    },
    customerToken(value, oldValue) {
      const doRefetch = value !== oldValue;
      this.refetchArticle(doRefetch);
    },
    isFetchPending(isPending) {
      if (!isPending && this.shouldBeRefetched) {
        this.$nextTick(() => {
          this.shouldBeRefetched = false;
          this.$fetch();
        });
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();
    this.resetPageHeaderBottomNavigation();

    next();
  },
  beforeDestroy() {
    this.$store.commit('article/setActiveArticle', null);
    this.$store.commit('category/setCategoryData', null);
  },
  beforeMount() {
    if (this.initialArticle && !this.isMultimedijaArticle) {
      window.scrollTo(0, 0);
    }
  },
  async mounted() {
    const { externalId: channelId } = this.$channelConfig('settings');
    const { application } = this.$channelConfig('settings');

    // Refetch first article for:
    // 1. article for allowed IP-s
    // 2. for IOS applications to by pass apple review rules
    // 3. elisa raamat
    const doRefetch = this.initialArticle && application.specialFeatures.forceArticleRefetch;
    this.refetchArticle(doRefetch);

    // Get articleExtraContenct
    const articleExtraContentService = new ArticleExtraContentService();
    const articleExtraContent = await articleExtraContentService.fetch({ channelId });
    this.$store.commit('article/setArticleExtraContent', articleExtraContent || null);

    // Get PT articles and headlines
    const bottom = articleExtraContent?.pt_bottom || null;
    const middle = articleExtraContent?.pt_middle || null;
    const ptArticles = middle ? bottom?.concat(middle) : bottom || null;
    if (Array.isArray(ptArticles) && ptArticles.length) {
      this.$store.commit('article/setArticlePtHeadlines', await this.getArticleHeadlines(ptArticles));
    }

    // Get Top headlines
    const articleTopHeadlinesList = this.$channelConfig('page').article.component.topHeadlines.globalTopHeadlinesIds;
    if (Array.isArray(articleTopHeadlinesList) && articleTopHeadlinesList.length) {
      this.$store.commit('article/setArticleTopHeadlines', await this.getArticleHeadlines(articleTopHeadlinesList));
    }

    // Get cxense articles
    const widgetId = this.$channelConfig('page').article.component.cXense?.widgetId;
    if (widgetId) {
      const articleCxenseService = new ArticleCxenseService();
      const cXenseArticles = await articleCxenseService.fetch({ widgetId });
      if (cXenseArticles.length) {
        this.$store.commit('article/setArticleCxenseArticles', cXenseArticles);
        this.$store.commit('article/setArticleCxenseHeadlines', await this.getArticleHeadlines(cXenseArticles.map((article) => Number(article.id))));
      }
    }

    if (!this.isRefetched) {
      this.isArticleFetching = false;
    }
  },
  jsonld() {
    if (!this.article) {
      return null;
    }

    const { base } = this.$channelConfig('meta');
    return articleJsonld(base, this.article);
  },
  methods: {
    handleArticleLoad(articleId) {
      this.$emit('article-loaded', !!articleId);
    },
    refetchArticle(doRefetch) {
      if (!!this.$fetch && doRefetch && !this.$route.query.preview) {
        /*
          this.$fetch() can't be called when it's already pending
          in edge cases, when article has to be refetched 2 times
          (Logged in from iOS app. 1st refetch - special feature, 2nd - customerToken watcher)
          2nd time will be not called at all, because 1st is already pending
        */
        if (!this.isFetchPending) {
          this.isRefetched = true;

          this.$nextTick(() => {
            this.$fetch();
          });
        } else {
          this.shouldBeRefetched = true;
        }
      }
    },
    handleArticleRequestFailure({ article, access, articleToken, customerToken }) {
      if (process.server) {
        return;
      }
      // Fallback check if article request fails
      const emptyBody = article.content.paywall.enabled && access && !(article.content.body.content.length > 0);
      const { externalId: channelId } = this.$channelConfig('settings');
      const { articleId } = this;

      if (emptyBody && !this.requestFailed) {
        this.requestFailed = true;
        this.$sentry.captureException(
          `Article fetch with no body - articleId: ${articleId}, channelId: ${channelId}, paywallToken: ${articleToken}, userToken: ${customerToken}`,
          { contexts: { vue: { componentName: 'Article' } }, tags: { 'process.server': process.server ? 'server' : 'client' } }
        );
      }
    },
    async getArticleHeadlines(headlineIds, limit = 0) {
      const universalHeadlinesService = new UniversalHeadlinesService();
      const headlines = await universalHeadlinesService.fetch({ id: headlineIds, limit });
      return headlines.items;
    },
  },
});
